import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import ketoBackground from '../../images/keto.png';
import ketoBuildImage from '../../images/keto-build.jpg';
import ketoAllowedProhibitedImage from '../../images/keto-allowed-prohibited.webp';
import ketoFoodsImage from '../../images/keto-foods.jpg';

const PAGE_URL = 'https://createadish.com/dietary-guide/keto';
const PAGE_TITLE = 'Keto (Ketogenic) Diet Guide: Low-Carb, High-Fat - Create A Dish';
const PAGE_DESCRIPTION = 'A very low-carb, high-fat diet designed to put the body into a state of ketosis, where it burns fat for fuel instead of glucose.';
const PAGE_IMAGE = 'https://createadish.com/images/keto-og.png';

const Keto: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="keto diet, ketogenic diet, low carb, high fat, ketosis, weight loss" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Keto (Ketogenic) Diet Guide: Low-Carb, High-Fat",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "LowCarb, WeightLoss, HighFat"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${ketoBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>Keto (Ketogenic) Diet</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>Low-Carb, High-Fat</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Keto Diet
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The Keto (Ketogenic) Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#LowCarb', '#WeightLoss', '#HighFat'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Keto (Ketogenic) Diet, a very low-carb, high-fat eating plan designed to induce ketosis—a metabolic state where your body burns fat for fuel instead of glucose. This diet has surged in popularity for its potential in weight loss and health management, but it requires careful planning to maintain nutritional balance. Our AI recipe generator can help you create keto-friendly dishes tailored to your needs—simply specify "low-carb, high-fat" options for meals like creamy avocado salads or buttery salmon.
          </Typography>
          <Box component="img" src={ketoBuildImage} alt="Keto diet basics" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The ketogenic diet traces its origins to the early 20th century, initially developed as a therapeutic tool for epilepsy. In 1921, Dr. Russell Wilder at the Mayo Clinic coined the term "ketogenic diet" to describe a high-fat, low-carb regimen that mimics fasting to control seizures in children. By the 1990s, it gained mainstream attention and today is widely adopted for weight loss.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The keto diet typically limits carbs to 5-10% of daily calories (about 20-50 grams), with 70-80% from fats and 10-20% from proteins.
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Healthy Fats</strong>: Avocados, olive oil, coconut oil, butter, fatty nuts.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Proteins</strong>: Fatty fish (salmon, tuna), meat (beef, pork), poultry, eggs.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Low-Carb Vegetables</strong>: Leafy greens, broccoli, zucchini, cauliflower, bell peppers.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Other</strong>: Nuts, seeds, and low-carb fruits like berries in moderation.</Typography>
            </Box>
            <Box component="img" src={ketoAllowedProhibitedImage} alt="Keto allowed and prohibited foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Foods to Limit or Avoid
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>High-carb items: Grains (bread, pasta, rice), sugars, starchy veggies (potatoes).</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Most fruits, beans, legumes, and processed foods.</Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Weight Loss</strong>: Promotes rapid fat burning and appetite suppression.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Improved Metabolic Health</strong>: Lowers blood sugar, cholesterol, and blood pressure.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Epilepsy Management</strong>: Proven effective for reducing seizures.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Mental Health and Energy</strong>: May enhance focus and reduce inflammation.</Typography>
            </Box>
            <Box component="img" src={ketoFoodsImage} alt="Keto foods overview" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Keto Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Start Gradually</strong>: Ease in by reducing carbs slowly to minimize "keto flu."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Stay Hydrated</strong>: Drink plenty of water and electrolytes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Plan Meals</strong>: Stock up on keto staples and prep ahead.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Incorporate AI</strong>: Generate recipes on CreateADish.com, like "keto butter chicken."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Monitor Health</strong>: Consult a doctor and watch for side effects.</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Keto;
